import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Typography,
  message,
  Segmented,
  Space,
  ConfigProvider,
  Result,
} from "antd";
import {
  CoffeeOutlined,
  CheckCircleOutlined,
  RedoOutlined,
  DeleteOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { generateBoard, Difficulty } from "./sudokuLogic";
import "./Sudoku.css";

const { Title } = Typography;

const Sudoku: React.FC = () => {
  const [grid, setGrid] = useState<number[][]>([]);
  const [solution, setSolution] = useState<number[][]>([]);
  const [initialIndices, setInitialIndices] = useState<boolean[][]>([]);
  const [selectedCell, setSelectedCell] = useState<[number, number] | null>(
    null,
  );
  const [difficulty, setDifficulty] = useState<Difficulty>("easy");
  const [isGameWon, setIsGameWon] = useState(false); // New state to track if puzzle is solved

  const startNewGame = (level: Difficulty = difficulty) => {
    const { initial, solution } = generateBoard(level);
    setGrid(initial);
    setSolution(solution);
    setInitialIndices(initial.map((row) => row.map((cell) => cell !== 0)));
    setSelectedCell(null);
    setIsGameWon(false); // Reset win state for new game
  };

  useEffect(() => {
    startNewGame();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Automatic Win Detection Feature
  useEffect(() => {
    if (grid.length === 0 || isGameWon) return;

    const isFull = grid.every((row) => row.every((cell) => cell !== 0));
    if (isFull) {
      if (JSON.stringify(grid) === JSON.stringify(solution)) {
        setIsGameWon(true);
        message.success({
          content: "Wonderful! You've completed the puzzle! ☕",
          icon: <SmileOutlined style={{ color: "#8d6e63" }} />,
          duration: 5,
        });
      }
    }
  }, [grid, solution, isGameWon]);

  const handleCellClick = (row: number, col: number) => {
    if (!initialIndices[row][col] && !isGameWon) setSelectedCell([row, col]);
  };

  const handleKeyPress = (num: number) => {
    if (selectedCell && !isGameWon) {
      const [r, c] = selectedCell;
      const newGrid = [...grid.map((row) => [...row])];
      newGrid[r][c] = num;
      setGrid(newGrid);
    }
  };

  const isRelated = (r: number, c: number) => {
    if (!selectedCell) return false;
    const [selR, selC] = selectedCell;
    const isSameBox =
      Math.floor(r / 3) === Math.floor(selR / 3) &&
      Math.floor(c / 3) === Math.floor(selC / 3);
    return r === selR || c === selC || isSameBox;
  };

  const hasConflict = (r: number, c: number, value: number) => {
    if (value === 0 || isGameWon) return false;
    for (let i = 0; i < 9; i++) {
      if (i !== c && grid[r][i] === value) return true;
      if (i !== r && grid[i][c] === value) return true;
    }
    return false;
  };

  const checkWinManual = () => {
    if (JSON.stringify(grid) === JSON.stringify(solution)) {
      setIsGameWon(true);
      message.success("Beautifully done! ☕");
    } else {
      message.warning("Not quite there yet. Keep brewing!");
    }
  };

  return (
    <ConfigProvider
      theme={{ token: { borderRadius: 12, colorPrimary: "#8d6e63" } }}
    >
      <div className="sudoku-page-wrapper">
        <div className="sudoku-container">
          <div className="header-section">
            <Title level={2} className="game-title">
              <CoffeeOutlined /> Cozy Sudoku
            </Title>
            <div className="level-chooser-wrapper">
              <Segmented
                block
                disabled={isGameWon}
                options={[
                  { label: "Easy", value: "easy" },
                  { label: "Medium", value: "medium" },
                  { label: "Hard", value: "hard" },
                ]}
                value={difficulty}
                onChange={(value) => {
                  setDifficulty(value as Difficulty);
                  startNewGame(value as Difficulty);
                }}
                className="custom-segmented"
              />
            </div>
          </div>

          <Card className="sudoku-card" bordered={false}>
            {isGameWon ? (
              <Result
                icon={<SmileOutlined style={{ color: "#8d6e63" }} />}
                title="Puzzle Solved!"
                subTitle="You've mastered this level. Time for another coffee?"
                extra={
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => startNewGame()}
                  >
                    Play Again
                  </Button>
                }
              />
            ) : (
              <>
                <div className="sudoku-grid-container">
                  <div className="sudoku-grid">
                    {grid.map((row, rowIndex) => (
                      <div key={rowIndex} className="sudoku-row">
                        {row.map((cell, colIndex) => (
                          <div
                            key={colIndex}
                            className={`sudoku-cell 
                              ${selectedCell?.[0] === rowIndex && selectedCell?.[1] === colIndex ? "selected" : ""}
                              ${isRelated(rowIndex, colIndex) ? "related" : ""}
                              ${initialIndices[rowIndex][colIndex] ? "fixed" : "editable"}
                              ${hasConflict(rowIndex, colIndex, cell) ? "conflict" : ""}
                              ${(rowIndex + 1) % 3 === 0 && rowIndex !== 8 ? "border-bottom" : ""}
                              ${(colIndex + 1) % 3 === 0 && colIndex !== 8 ? "border-right" : ""}
                            `}
                            onClick={() => handleCellClick(rowIndex, colIndex)}
                          >
                            <span>{cell !== 0 ? cell : ""}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="keypad">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <Button
                      key={num}
                      onClick={() => handleKeyPress(num)}
                      className="key-btn"
                    >
                      {num}
                    </Button>
                  ))}
                  <Button
                    onClick={() => handleKeyPress(0)}
                    icon={<DeleteOutlined />}
                    className="key-btn clear-btn"
                  />
                </div>

                <Space className="action-buttons" wrap>
                  <Button
                    icon={<RedoOutlined />}
                    onClick={() => startNewGame()}
                  >
                    Reset
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={checkWinManual}
                    className="check-btn"
                  >
                    Check Answer
                  </Button>
                </Space>
              </>
            )}
          </Card>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Sudoku;
